import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import type { BlockLink, IContactMapEmbedBlockProps } from "./ContactMapEmbedBlock.props";
import { RichTextHtml } from "../../RichTextHtml";
import { KaajAction } from "../_shared/KaajAction";

const Link = resolveKaajPrimitive("Link");

const isNonEmptyString = (value?: string | null): value is string =>
  typeof value === "string" && value.trim().length > 0;

const formatAddressLines = (address: IContactMapEmbedBlockProps["address"]) => {
  if (!address) {
    return [];
  }

  const locality = [address.city, [address.region, address.postalCode].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

  return [address.line1, address.line2, locality, address.country].filter(isNonEmptyString);
};

const isExternalHref = (href: string): boolean => /^https?:\/\//i.test(href);

const getRel = (link: BlockLink): string | undefined => {
  if (link.href && (link.target === "_blank" || isExternalHref(link.href))) {
    return "noopener noreferrer";
  }

  return undefined;
};

const MapPinIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.8"
    viewBox="0 0 24 24"
  >
    <path d="M12 21s6-5.35 6-11a6 6 0 0 0-12 0c0 5.65 6 11 6 11Z" />
    <circle cx="12" cy="10" r="2" />
  </svg>
);

const DirectionLink = ({
  className,
  compact = false,
  link,
}: {
  className?: string;
  compact?: boolean;
  link: BlockLink;
}) => {
  if (!link.href || !link.text) return null;

  return (
    <Link
      aria-label={link.ariaLabel ?? link.text ?? undefined}
      className={cn(
        "inline-flex min-h-11 max-w-full items-center justify-center rounded-full border border-[#2144FF] bg-[#2144FF] px-5 py-2.5 text-center text-sm font-extrabold text-white shadow-[0_14px_32px_rgba(33,68,255,0.24)] transition-[background-color,border-color,box-shadow,color,transform] duration-200 [overflow-wrap:anywhere] hover:border-[#132EDB] hover:bg-[#132EDB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-4 motion-reduce:transition-none motion-safe:hover:-translate-y-0.5",
        compact &&
          "px-4 py-2 text-xs shadow-[0_10px_24px_rgba(33,68,255,0.2)] focus-visible:ring-offset-[#F8FAFF]",
        className,
      )}
      href={link.href}
      rel={getRel(link)}
      target={link.target ?? undefined}
    >
      <span>{link.text}</span>
      <span aria-hidden="true" className="ml-2">
        ↗
      </span>
    </Link>
  );
};

const MapFrame = ({ mapEmbed }: { mapEmbed: IContactMapEmbedBlockProps["mapEmbed"] }) => {
  const embed = mapEmbed;

  if (!embed || !isNonEmptyString(embed.src)) {
    return null;
  }

  const providerName = embed.provider;

  return (
    <figure className="relative flex h-full min-w-0 flex-col overflow-hidden rounded-[1.65rem] border border-[#CAD7FF] bg-[#EEF2FF] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] sm:rounded-[1.85rem] sm:p-3">
      <div className="relative isolate min-h-[20rem] flex-1 overflow-hidden rounded-[1.25rem] border border-white/80 bg-white sm:rounded-[1.45rem]">
        <iframe
          className="absolute inset-0 z-10 h-full w-full border-0 bg-white saturate-[1.05] contrast-[1.04]"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          src={embed.src}
          title={embed.title}
        />

        {providerName ? (
          <div className="pointer-events-none absolute left-4 top-4 z-20 inline-flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-full border border-white/80 bg-white/94 px-3 py-1.5 text-xs font-extrabold text-[#2144FF] shadow-[0_10px_24px_rgba(21,25,39,0.12)] [overflow-wrap:anywhere]">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#EEF2FF] text-[#2144FF]">
              <MapPinIcon className="size-3.5" />
            </span>
            {providerName}
          </div>
        ) : null}
      </div>
    </figure>
  );
};

export const ContactMapEmbedBlock = ({
  buttons,
  eyebrow,
  heading,
  parentClass,
  spacing,
  subheading,
  mapEmbed,
  address,
  fallbackDirectionsLink,
  anchorId,
}: IContactMapEmbedBlockProps) => {
  const eyebrowText = eyebrow;
  const headingText = heading;
  const subheadingText = subheading;
  const addressLines = formatAddressLines(address);
  const directions = fallbackDirectionsLink;
  const hasMap = isNonEmptyString(mapEmbed?.src);
  const sectionId = anchorId;
  const headingId = headingText && sectionId ? `${sectionId}-heading` : undefined;
  const hasContent = Boolean(
    eyebrowText ||
    headingText ||
    subheadingText ||
    hasMap ||
    addressLines.length > 0 ||
    (directions?.href && directions.text) ||
    buttons?.length,
  );

  if (!hasContent) {
    return null;
  }

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative isolate overflow-hidden text-[#151927]",
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      data-block-handle="contactMapEmbed"
      id={sectionId ?? undefined}
    >
      <div className="kaaj-container relative">
        <div
          className={cn(
            "relative overflow-hidden rounded-[2.25rem] border border-[#D9E1FF] bg-white/94 p-3 shadow-[0_28px_88px_rgba(33,68,255,0.12)] sm:p-4 lg:gap-4 lg:p-5",
            hasMap
              ? "lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(24rem,0.8fr)]"
              : "mx-auto max-w-3xl",
          )}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-[#B8C7FF] to-transparent"
          />

          {hasMap ? <MapFrame mapEmbed={mapEmbed} /> : null}

          <aside
            className={cn(
              "relative flex min-w-0 flex-col justify-between overflow-hidden rounded-[1.75rem] border border-[#D9E1FF] bg-[linear-gradient(180deg,#FFFFFF_0%,#FAFBFF_100%)] p-6 shadow-[0_18px_52px_rgba(33,68,255,0.07)] sm:p-7",
              hasMap ? "mt-3 lg:mt-0" : "mt-0",
            )}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-16 top-16 -z-10 size-40 rounded-full bg-[#D9E1FF]/58 blur-3xl"
            />
            <div className="min-w-0">
              {eyebrowText ? (
                <p className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#CAD7FF] bg-[#EEF2FF] px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2144FF] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] [overflow-wrap:anywhere]">
                  <span aria-hidden="true" className="size-2 rounded-full bg-[#55D6C2]" />
                  {eyebrowText}
                </p>
              ) : null}
              {headingText ? (
                <h2 className="kaaj-section-heading mt-4 text-[#151927]" id={headingId}>
                  {headingText}
                </h2>
              ) : null}
              {subheadingText ? (
                <RichTextHtml
                  className="mt-5 max-w-[60ch] text-pretty text-base leading-7 text-[#4B5568] [overflow-wrap:anywhere] sm:text-lg sm:leading-8"
                  html={subheadingText}
                />
              ) : null}

              {addressLines.length > 0 ? (
                <div className="mt-8 rounded-[1.45rem] border border-[#D9E1FF] bg-[#F8FAFF] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] sm:p-6">
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-[#CAD7FF] bg-white text-[#2144FF] shadow-[0_10px_24px_rgba(33,68,255,0.08)]"
                    >
                      <MapPinIcon className="size-5" />
                    </span>
                    <div className="min-w-0">
                      <address className="text-base not-italic leading-7 text-[#151927] [overflow-wrap:anywhere] sm:text-lg sm:leading-8">
                        {addressLines.map((line, index) => (
                          <span className="block" key={`${line}-${index}`}>
                            {line}
                          </span>
                        ))}
                      </address>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {directions?.href && directions.text ? (
              <div className="mt-7 border-t border-[#D9E1FF] pt-6">
                <DirectionLink className="w-full sm:w-auto" link={directions} />
              </div>
            ) : null}
            {buttons?.length ? (
              <div className="mt-4 flex flex-wrap gap-3">
                {buttons.map((button, index) => (
                  <KaajAction
                    ariaLabel={button.ariaLabel}
                    href={button.href}
                    key={`${button.href}-${button.text}-${index}`}
                    label={button.text}
                    target={button.target}
                    variant={button.style}
                  />
                ))}
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </section>
  );
};

ContactMapEmbedBlock.displayName = "Block::ContactMapEmbedBlock";
